import { supabase } from "@/lib/supabase";
import { personalizationService, type PersonalizedQuote } from "./personalizationService";

export interface Quote {
  id: string;
  text: string;
  author: string;
  expertise: string;
  topics: string[];
  worldviewTags: string[];
}

export interface AuthorPreference {
  userId: string;
  moreAuthors: string[];
  lessAuthors: string[];
}

interface UserContext {
  expertise: string[];
  goals: string[];
  worldview: string | null;
  background: string | null;
  tone: string | null;
  learningStyle: string | null;
  recentThreadTopics: string[];
  recentThreadThemes: string[];
}

const CURATED_QUOTES: Quote[] = [
  { id: "1", text: "The only way to do great work is to love what you do.", author: "Steve Jobs", expertise: "Innovation", topics: ["productivity", "passion", "work"], worldviewTags: ["growth", "purpose"] },
  { id: "2", text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill", expertise: "Leadership", topics: ["resilience", "courage", "success"], worldviewTags: ["perseverance", "strength"] },
  { id: "3", text: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt", expertise: "Vision", topics: ["dreams", "future", "belief"], worldviewTags: ["optimism", "hope"] },
  { id: "4", text: "Knowledge is power. Information is liberating.", author: "Kofi Annan", expertise: "Knowledge", topics: ["learning", "knowledge", "education"], worldviewTags: ["empowerment", "wisdom"] },
  { id: "5", text: "An investment in knowledge pays the best interest.", author: "Benjamin Franklin", expertise: "Learning", topics: ["learning", "investment", "growth"], worldviewTags: ["wisdom", "practical"] },
  { id: "6", text: "The mind is everything. What you think you become.", author: "Buddha", expertise: "Mindfulness", topics: ["mindset", "meditation", "consciousness"], worldviewTags: ["spiritual", "inner peace"] },
  { id: "7", text: "In the middle of difficulty lies opportunity.", author: "Albert Einstein", expertise: "Science", topics: ["problem-solving", "science", "innovation"], worldviewTags: ["optimism", "analytical"] },
  { id: "8", text: "The greatest glory in living lies not in never falling, but in rising every time we fall.", author: "Nelson Mandela", expertise: "Justice", topics: ["resilience", "justice", "leadership"], worldviewTags: ["perseverance", "equality"] },
  { id: "9", text: "Your time is limited, don't waste it living someone else's life.", author: "Steve Jobs", expertise: "Innovation", topics: ["authenticity", "life", "purpose"], worldviewTags: ["individuality", "purpose"] },
  { id: "10", text: "The only limit to our realization of tomorrow will be our doubts of today.", author: "Franklin D. Roosevelt", expertise: "Leadership", topics: ["confidence", "future", "belief"], worldviewTags: ["optimism", "courage"] },
  { id: "11", text: "Education is the most powerful weapon which you can use to change the world.", author: "Nelson Mandela", expertise: "Justice", topics: ["education", "change", "empowerment"], worldviewTags: ["equality", "progress"] },
  { id: "12", text: "The best way to predict the future is to create it.", author: "Peter Drucker", expertise: "Business", topics: ["innovation", "future", "leadership"], worldviewTags: ["proactive", "entrepreneurship"] },
  { id: "13", text: "It is during our darkest moments that we must focus to see the light.", author: "Aristotle", expertise: "Philosophy", topics: ["resilience", "hope", "mindset"], worldviewTags: ["wisdom", "perseverance"] },
  { id: "14", text: "The only thing we have to fear is fear itself.", author: "Franklin D. Roosevelt", expertise: "Leadership", topics: ["courage", "fear", "leadership"], worldviewTags: ["courage", "strength"] },
  { id: "15", text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt", expertise: "Leadership", topics: ["confidence", "belief", "motivation"], worldviewTags: ["optimism", "self-belief"] },
  { id: "16", text: "The purpose of our lives is to be happy.", author: "Dalai Lama", expertise: "Spirituality", topics: ["happiness", "purpose", "life"], worldviewTags: ["spiritual", "peace"] },
  { id: "17", text: "Life is what happens when you're busy making other plans.", author: "John Lennon", expertise: "Creativity", topics: ["life", "presence", "mindfulness"], worldviewTags: ["creative", "philosophical"] },
  { id: "18", text: "Get busy living or get busy dying.", author: "Stephen King", expertise: "Writing", topics: ["life", "action", "motivation"], worldviewTags: ["practical", "urgency"] },
  { id: "19", text: "You only live once, but if you do it right, once is enough.", author: "Mae West", expertise: "Entertainment", topics: ["life", "authenticity", "joy"], worldviewTags: ["boldness", "individuality"] },
  { id: "20", text: "The unexamined life is not worth living.", author: "Socrates", expertise: "Philosophy", topics: ["reflection", "wisdom", "philosophy"], worldviewTags: ["wisdom", "introspection"] },
  { id: "21", text: "To be yourself in a world that is constantly trying to make you something else is the greatest accomplishment.", author: "Ralph Waldo Emerson", expertise: "Philosophy", topics: ["authenticity", "individuality", "self"], worldviewTags: ["individuality", "authenticity"] },
  { id: "22", text: "In three words I can sum up everything I've learned about life: it goes on.", author: "Robert Frost", expertise: "Poetry", topics: ["life", "resilience", "perspective"], worldviewTags: ["acceptance", "wisdom"] },
  { id: "23", text: "The way to get started is to quit talking and begin doing.", author: "Walt Disney", expertise: "Entertainment", topics: ["action", "productivity", "creativity"], worldviewTags: ["action-oriented", "entrepreneurship"] },
  { id: "24", text: "Innovation distinguishes between a leader and a follower.", author: "Steve Jobs", expertise: "Innovation", topics: ["innovation", "leadership", "creativity"], worldviewTags: ["innovation", "leadership"] },
  { id: "25", text: "Stay hungry, stay foolish.", author: "Steve Jobs", expertise: "Innovation", topics: ["curiosity", "growth", "learning"], worldviewTags: ["growth", "curiosity"] },
  { id: "26", text: "The only true wisdom is in knowing you know nothing.", author: "Socrates", expertise: "Philosophy", topics: ["humility", "wisdom", "learning"], worldviewTags: ["wisdom", "humility"] },
  { id: "27", text: "What you do speaks so loudly that I cannot hear what you say.", author: "Ralph Waldo Emerson", expertise: "Philosophy", topics: ["action", "integrity", "character"], worldviewTags: ["authenticity", "action-oriented"] },
  { id: "28", text: "The secret of getting ahead is getting started.", author: "Mark Twain", expertise: "Writing", topics: ["action", "productivity", "motivation"], worldviewTags: ["practical", "proactive"] },
  { id: "29", text: "You must be the change you wish to see in the world.", author: "Mahatma Gandhi", expertise: "Leadership", topics: ["change", "leadership", "responsibility"], worldviewTags: ["purpose", "transformation"] },
  { id: "30", text: "The harder I work, the luckier I get.", author: "Samuel Goldwyn", expertise: "Business", topics: ["work", "effort", "success"], worldviewTags: ["practical", "perseverance"] },
];

class QuotesService {
  async getAuthorPreferences(userId: string): Promise<AuthorPreference | null> {
    try {
      const { data, error } = await supabase
        .from("user_quote_author_preferences")
        .select("id, user_id, more_authors, less_authors, created_at, updated_at")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching author preferences:", error);
        return null;
      }

      if (data) {
        return {
          userId: data.user_id,
          moreAuthors: data.more_authors || [],
          lessAuthors: data.less_authors || [],
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching author preferences:", error);
      return null;
    }
  }

  async updateAuthorPreference(
    userId: string,
    author: string,
    preference: "more" | "less"
  ): Promise<void> {
    try {
      const existing = await this.getAuthorPreferences(userId);
      
      let moreAuthors = existing?.moreAuthors || [];
      let lessAuthors = existing?.lessAuthors || [];

      moreAuthors = moreAuthors.filter(a => a !== author);
      lessAuthors = lessAuthors.filter(a => a !== author);

      if (preference === "more") {
        moreAuthors.push(author);
      } else {
        lessAuthors.push(author);
      }

      const payload = {
        user_id: userId,
        more_authors: moreAuthors,
        less_authors: lessAuthors,
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        await supabase
          .from("user_quote_author_preferences")
          .update(payload)
          .eq("user_id", userId);
      } else {
        await supabase
          .from("user_quote_author_preferences")
          .insert(payload);
      }
    } catch (error) {
      console.error("Error updating author preference:", error);
    }
  }

  async getUserContext(userId: string): Promise<UserContext> {
    try {
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("expertise, goals, worldview, background, tone, learning_style")
        .eq("user_id", userId)
        .single();

      const { data: threads } = await supabase
        .from("chat-threads")
        .select("title, topic, messages")
        .eq("userId", userId)
        .order("updatedAt", { ascending: false })
        .limit(10);

      const recentThreadTopics: string[] = [];
      const recentThreadThemes: string[] = [];

      threads?.forEach((thread: any) => {
        if (thread.topic) {
          recentThreadTopics.push(...thread.topic.toLowerCase().split(/[\s,]+/));
        }
        if (thread.title) {
          const titleWords = thread.title.toLowerCase().split(/[\s,]+/).filter((w: string) => w.length > 3);
          recentThreadTopics.push(...titleWords);
        }
        
        const messages = Array.isArray(thread.messages) ? thread.messages : [];
        messages.slice(-3).forEach((msg: any) => {
          if (msg.content && typeof msg.content === 'string') {
            const keywords = this.extractKeyThemes(msg.content);
            recentThreadThemes.push(...keywords);
          }
        });
      });

      return {
        expertise: profile?.expertise || [],
        goals: profile?.goals || [],
        worldview: profile?.worldview || null,
        background: profile?.background || null,
        tone: profile?.tone || null,
        learningStyle: profile?.learning_style || null,
        recentThreadTopics: [...new Set(recentThreadTopics)].slice(0, 20),
        recentThreadThemes: [...new Set(recentThreadThemes)].slice(0, 30),
      };
    } catch (error) {
      console.error("Error fetching user context:", error);
      return {
        expertise: [],
        goals: [],
        worldview: null,
        background: null,
        tone: null,
        learningStyle: null,
        recentThreadTopics: [],
        recentThreadThemes: [],
      };
    }
  }

  private extractKeyThemes(content: string): string[] {
    const themeKeywords = [
      "productivity", "success", "growth", "learning", "leadership", "innovation",
      "creativity", "motivation", "resilience", "mindset", "focus", "goals",
      "business", "career", "health", "wellness", "relationships", "money",
      "investing", "technology", "science", "philosophy", "spirituality",
      "happiness", "purpose", "meaning", "change", "improvement", "strategy",
      "problem", "solution", "challenge", "opportunity", "skill", "knowledge",
      "research", "analysis", "decision", "planning", "execution", "progress"
    ];
    
    const lowerContent = content.toLowerCase();
    return themeKeywords.filter(keyword => lowerContent.includes(keyword));
  }

  async getRecentThreadTopics(userId: string): Promise<string[]> {
    const context = await this.getUserContext(userId);
    return context.recentThreadTopics;
  }

  async getPersonalizedQuotes(
    userId: string,
    count: number = 5
  ): Promise<Quote[]> {
    try {
      const aiQuotes = await personalizationService.generatePersonalizedQuotes(userId, count);
      
      if (aiQuotes && aiQuotes.length > 0) {
        return aiQuotes.map((q: PersonalizedQuote) => ({
          id: q.id,
          text: q.text,
          author: q.author,
          expertise: q.expertise,
          topics: [q.topic],
          worldviewTags: [q.topic],
        }));
      }
    } catch (error) {
      console.error("AI quote generation failed, falling back to curated:", error);
    }

    const [preferences, userContext] = await Promise.all([
      this.getAuthorPreferences(userId),
      this.getUserContext(userId),
    ]);

    let availableQuotes = [...CURATED_QUOTES];

    if (preferences?.lessAuthors.length) {
      availableQuotes = availableQuotes.filter(
        q => !preferences.lessAuthors.includes(q.author)
      );
    }

    const scoredQuotes = availableQuotes.map(quote => {
      let score = 0;

      if (preferences?.moreAuthors.includes(quote.author)) {
        score += 15;
      }

      if (userContext.expertise.length > 0) {
        const expertiseMatches = userContext.expertise.filter(e =>
          quote.expertise.toLowerCase().includes(e.toLowerCase()) ||
          quote.topics.some(t => e.toLowerCase().includes(t) || t.includes(e.toLowerCase()))
        ).length;
        score += expertiseMatches * 8;
      }

      if (userContext.goals.length > 0) {
        const goalMatches = userContext.goals.filter(g =>
          quote.topics.some(t => g.toLowerCase().includes(t) || t.includes(g.toLowerCase())) ||
          quote.worldviewTags.some(w => g.toLowerCase().includes(w) || w.includes(g.toLowerCase()))
        ).length;
        score += goalMatches * 6;
      }

      if (userContext.worldview) {
        const worldviewWords = userContext.worldview.toLowerCase().split(/[\s,]+/);
        const worldviewMatches = quote.worldviewTags.filter(w =>
          worldviewWords.some(ww => ww.includes(w) || w.includes(ww))
        ).length;
        score += worldviewMatches * 5;
      }

      if (userContext.recentThreadTopics.length > 0) {
        const topicMatches = quote.topics.filter(t =>
          userContext.recentThreadTopics.some(rt => rt.includes(t) || t.includes(rt))
        ).length;
        score += topicMatches * 4;
      }

      if (userContext.recentThreadThemes.length > 0) {
        const themeMatches = quote.topics.filter(t =>
          userContext.recentThreadThemes.includes(t)
        ).length;
        score += themeMatches * 3;
        
        const worldviewThemeMatches = quote.worldviewTags.filter(w =>
          userContext.recentThreadThemes.some(rt => rt.includes(w) || w.includes(rt))
        ).length;
        score += worldviewThemeMatches * 2;
      }

      score += Math.random() * 1.5;

      return { quote, score };
    });

    scoredQuotes.sort((a, b) => b.score - a.score);

    return scoredQuotes.slice(0, count).map(sq => sq.quote);
  }

  clearQuotesCache(userId: string): void {
    personalizationService.clearCache(userId);
  }

  getDefaultQuotes(): Quote[] {
    const shuffled = [...CURATED_QUOTES].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 5);
  }
}

export const quotesService = new QuotesService();
